import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { DAM_SVG } from 'components/Dam/DamAssets';
import { ButtonC } from 'Common/Buttons';

const DateRangePicker = ({ startDate, setStartDate, endDate, setEndDate }) => {
  //   const [startDate, setStartDate] = useState(null);
  //   const [endDate, setEndDate] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Independent month states for each calendar
  const [leftCalendarDate, setLeftCalendarDate] = useState(moment().startOf('month'));
  const [rightCalendarDate, setRightCalendarDate] = useState(
    moment().add(1, 'month').startOf('month'),
  );

  const formatDisplayDate = () => {
    if (startDate && endDate) {
      return `${moment(startDate).format('DD/MM/YYYY')} - ${moment(endDate).format('DD/MM/YYYY')}`;
    } else if (startDate) {
      return moment(startDate).format('DD/MM/YYYY');
    }
    return '';
  };

  const handleInputClick = () => {
    setIsOpen(!isOpen);
  };

  const handleDone = () => {
    setIsOpen(false);
  };

  const handleDateClick = (date) => {
    const clickedDate = moment(date);

    if (!startDate || (startDate && endDate)) {
      // Start new selection
      setStartDate(clickedDate.toDate());
      setEndDate(null);
    } else {
      // Complete the range
      if (clickedDate.isBefore(startDate)) {
        setStartDate(clickedDate.toDate());
        setEndDate(null);
      } else {
        setEndDate(clickedDate.toDate());
      }
    }
  };

  const isDateSelected = (date) => {
    const momentDate = moment(date);
    return (
      (startDate && momentDate.isSame(moment(startDate), 'day')) ||
      (endDate && momentDate.isSame(moment(endDate), 'day'))
    );
  };

  const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    const momentDate = moment(date);
    return momentDate.isBetween(moment(startDate), moment(endDate), 'day', '[]');
  };

  const navigateCalendar = (direction, isLeft) => {
    if (isLeft) {
      setLeftCalendarDate((prev) => moment(prev).add(direction, 'month'));
    } else {
      setRightCalendarDate((prev) => moment(prev).add(direction, 'month'));
    }
  };

  const renderCalendar = (calendarDate, isLeft) => {
    const startOfMonth = moment(calendarDate).startOf('month');
    const endOfMonth = moment(calendarDate).endOf('month');
    const startOfCalendar = moment(startOfMonth).startOf('week');
    const endOfCalendar = moment(endOfMonth).endOf('week');

    const days = [];
    const current = moment(startOfCalendar);

    while (current.isSameOrBefore(endOfCalendar)) {
      days.push(moment(current));
      current.add(1, 'day');
    }

    return (
      <div className="calendar-card">
        {/* Calendar Header */}
        <div className="calendar-header">
          <button onClick={() => navigateCalendar(-1, isLeft)} className="nav-button">
            {DAM_SVG.additionSvgIcon}
          </button>
          <span className="month-year">{calendarDate.format('MMM YYYY')}</span>
          <button onClick={() => navigateCalendar(1, isLeft)} className="nav-button">
            {DAM_SVG.additionSvgIcon}
          </button>
        </div>

        {/* Days of week */}
        <div className="days-header">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
            <div key={day} className="day-header">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="calendar-grid">
          {days.map((day, index) => {
            const isCurrentMonth = day.isSame(calendarDate, 'month');
            const isSelected = isDateSelected(day.toDate());
            const isInRange = isDateInRange(day.toDate());

            return (
              <button
                key={index}
                onClick={() => isCurrentMonth && handleDateClick(day.toDate())}
                className={classNames('day-cell', {
                  'current-month': isCurrentMonth,
                  'other-month': !isCurrentMonth,
                  selected: isSelected,
                  'in-range': isInRange && !isSelected,
                  clickable: isCurrentMonth,
                })}
                disabled={!isCurrentMonth}
              >
                {day.format('D')}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const ChevronLeft = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="15,18 9,12 15,6"></polyline>
    </svg>
  );

  const ChevronRight = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="9,18 15,12 9,6"></polyline>
    </svg>
  );

  const CalendarIcon = () => (
    <svg
      className="calendar-icon"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );

  const customStyles = `
    .date-range-container {
      max-width: 768px;
      margin: 0 auto;
      padding: 24px;
      background-color: #f9fafb;
      min-height: 100vh;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    
    .date-range-card {
      background-color: white;
      border-radius: 8px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e7eb;
      padding: 24px;
      position: relative;
    }
    
    .date-range-title {
      font-size: 18px;
      font-weight: 600;
      color: #374151;
      margin-bottom: 16px;
    }
    
    .date-input-container {
      margin-bottom: 24px;
      position: relative;
    }
    
    .date-input-wrapper {
      position: relative;
    }
    
    .date-input {
      width: 100%;
      padding: 8px 12px 8px 40px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 14px;
      outline: none;
      cursor: pointer;
      transition: all 0.2s;
      background-color: white;
    }
    
    .date-input:focus {
      border-color: #753cb7;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    .calendar-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
      pointer-events: none;
      width: 18px;
      height: 18px;
    }
    
    .custom-datepicker-wrapper {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      margin-top: 8px;
      background-color: white;
      border: 1px solid #ededed;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
      z-index: 50;
      padding: 24px;
      width: fit-content
    }
    
    .calendars-container {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      margin-bottom: 24px;
    }
    
    @media (max-width: 768px) {
      .calendars-container {
        grid-template-columns: 1fr;
      }
    }
    
    .calendar-card {
      background-color: white;
      border: 1px solid #e5e7eb;
      padding: 16px;
    }
    
    .calendar-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
    }
    
    .nav-button {
      padding: 4px;
      background: none;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      color: #6b7280;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .nav-button:hover {
      background-color: #f3f4f6;
      color: #374151;
    }
    
    .month-year {
      font-weight: 500;
      color: #374151;
      font-size: 16px;
    }
    
    .days-header {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 4px;
      margin-bottom: 8px;
    }
    
    .day-header {
      text-align: center;
      font-size: 12px;
      font-weight: 500;
      color: #6b7280;
      padding: 8px 0;
    }
    
    .calendar-grid {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 4px;
    }
    
    .day-cell {
      aspect-ratio: 1;
      border: none;
      background: none;
      border-radius: 4px;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .day-cell.current-month {
      color: #374151;
    }
    
    .day-cell.other-month {
      color: #d1d5db;
      cursor: not-allowed;
    }
    
    .day-cell.clickable:hover:not(.selected):not(.in-range) {
      background-color: #f3f4f6;
    }
    
    .day-cell.selected {
      background-color: #753cb7;
      color: white;
    }
    
    .day-cell.in-range {
      background-color: #eeeeee;
      color: #753cb7;
    }
    
    .done-button-container {
      display: flex;
      justify-content: flex-end;
    }
    
    .done-button {
      padding: 8px 24px;
      background-color: white;
      border: 1px solid #c084fc;
      color: #9333ea;
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 0.2s;
      font-size: 14px;
    }
    
    .done-button:hover {
      background-color: #f3e8ff;
    }
  `;

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <style>{customStyles}</style>
      <div className="">
        <div
          className=""
          style={{
            position: 'relative',
          }}
        >
          <label
            className="custom-label"
            style={{
              marginBottom: '0rem',
            }}
          >
            Date Range
          </label>

          {/* Date input field */}
          <div className="">
            <div
              className=""
              style={{
                width: '100%',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '10px', // adjust as needed
                  transform: 'translateY(-50%)', // only vertical centering
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {DAM_SVG.calendarSvg}
              </div>

              <input
                type="text"
                value={formatDisplayDate()}
                placeholder="DD/MM/YYYY - DD/MM/YYYY"
                className="lg-input Custom-input "
                onClick={handleInputClick}
                style={{
                  height: '38px',
                  padding: '0px 0px 0px 2rem',
                  paddingLeft: '2rem',
                }}
              />
            </div>
          </div>

          {/* Custom DatePicker dropdown */}
          {isOpen && (
            <div className="custom-datepicker-wrapper" ref={wrapperRef}>
              <div className="calendars-container">
                {renderCalendar(leftCalendarDate, true)}
                {renderCalendar(rightCalendarDate, false)}
              </div>

              {/* Done button */}
              <div className="done-button-container">
                {/* <button onClick={handleDone} className="done-button">
                  Done
                </button> */}

                <ButtonC
                  text="Done"
                  color="Secondary"
                  size="lg"
                  click={() => {
                    setIsOpen(!isOpen);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DateRangePicker;
